import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Public gallery grid (2/3 cols) + click-to-zoom lightbox with arrows.
 *
 * Props:
 *   images (string[]),
 *   title (default 'Galería'),
 *   testid (default 'profile-gallery')
 *   altPrefix (default 'Galería')
 */
export default function GalleryGrid({ images, title = 'Galería', testid = 'profile-gallery', altPrefix = 'Galería' }) {
  const list = Array.isArray(images) ? images : [];
  const [idx, setIdx] = useState(null);
  const open = idx !== null;

  const close = useCallback(() => setIdx(null), []);
  const next = useCallback((e) => { e?.stopPropagation?.(); setIdx(i => (i === null ? null : (i + 1) % list.length)); }, [list.length]);
  const prev = useCallback((e) => { e?.stopPropagation?.(); setIdx(i => (i === null ? null : (i - 1 + list.length) % list.length)); }, [list.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close, next, prev]);

  if (!list.length) return null;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6" data-testid={testid}>
      <h2 className="font-serif text-xl font-bold text-habita-dark mb-4">{title}</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
        {list.map((url, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            className="relative aspect-square rounded-xl overflow-hidden bg-habita-cream group focus:outline-none focus:ring-2 focus:ring-habita"
            data-testid={`${testid}-item-${i}`}
          >
            <img src={url} alt={`${altPrefix} ${i + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
          </button>
        ))}
      </div>

      {open && (
        <div
          onClick={close}
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          data-testid={`${testid}-lightbox`}
        >
          <button
            onClick={(e) => { e.stopPropagation(); close(); }}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            aria-label="Cerrar"
            data-testid={`${testid}-lightbox-close`}
          >
            <X className="w-5 h-5" />
          </button>
          {list.length > 1 && (
            <>
              <button
                onClick={prev}
                className="absolute left-3 sm:left-6 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                aria-label="Anterior"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={next}
                className="absolute right-3 sm:right-6 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                aria-label="Siguiente"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}
          <img
            src={list[idx]}
            alt=""
            className="max-w-[95vw] max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          {list.length > 1 && (
            <div className="absolute bottom-6 left-0 right-0 text-center text-white/80 text-sm">
              {idx + 1} / {list.length}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
